import { colors as basicColors } from 'basic-components';
import { CommonService } from 'dashboard-services/services';

import basicCustomInsightConsts from 'actions/modules/insight/custom/basicConsts';
import ChartSourceService from 'services/ChartSourceService';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc)

const EMPTY_ARRAY = []
const DAY_FORMAT = "YYYY-MM-DD"
const { BASIC_CHART_TYPES, CHART_SOURCES, CHART_TYPES, EXPIRY_DATE_REFERENCE_LINE_KEY, LINE_STYLES, REFERENCE_BAND_KEY_PREFIX, REFERENCE_BAND_DEFAULTS, REFERENCE_BAND_LABELS, REFERENCE_DATA_TYPES, REFERENCE_LINE_AXES, REFERENCE_LINE_KEY_PREFIX, REFERENCE_X_VALUES, REFERENCE_Y_VALUE_TYPES, SEASONALITY_TYPE, SPREAD_BAND_KEY, SPREAD_BAND_RANGE, SPREAD_LOWER_BAND_KEY, SPREAD_MEDIAN_KEY, SPREAD_UPPER_BAND_KEY, TILE_TYPES } = basicCustomInsightConsts
const LABEL_SEPARATOR = " · "
const CREATED_Y_VALUE_PATTERN = /^-?(\d+(\.\d+)?|\.\d+)$/

// The Reference data model of a chart tile: items, their keys, labels, default formatting and the legacy migration.
export default (() => {

  const isBandItem = item => item?.type === REFERENCE_DATA_TYPES.BAND.value

  const isLineItem = item => item?.type === REFERENCE_DATA_TYPES.LINE.value

  const isYAxisLine = item => isLineItem(item) && (item.axis === REFERENCE_LINE_AXES.LEFT.value || item.axis === REFERENCE_LINE_AXES.RIGHT.value)

  const isXValueLine = ({ item, xValue }) => isLineItem(item) && item.axis === REFERENCE_LINE_AXES.X.value && item.xValue === xValue

  const isExpiryLine = item => isXValueLine({ item, xValue: REFERENCE_X_VALUES.EXPIRY_DATE.value })

  const isPercentileLine = item => isYAxisLine(item) && item.yValueType === REFERENCE_Y_VALUE_TYPES.PERCENTILE && item.yValue !== undefined

  const isLineKey = value => typeof value === "string" && value.startsWith(REFERENCE_LINE_KEY_PREFIX)

  const isTodayLine = item => isXValueLine({ item, xValue: REFERENCE_X_VALUES.TODAY.value })

  // Today and Expiry date lines show their date on the chart until the user renames them.
  const isDateLabelLine = item => isExpiryLine(item) || isTodayLine(item)

  // Like the legacy expiry line: labelled on the chart, no legend entry.
  const DATE_LINE_FORMATTING = { showInLegend: false, withLabel: true }
  const REGULAR_LINE_FORMATTING = { showInLegend: true, withLabel: false }

  const getLineLabelFormatting = ({ item }) => isDateLabelLine(item) ? DATE_LINE_FORMATTING : REGULAR_LINE_FORMATTING

  const isBandEnabled = ({ referenceData }) => (referenceData || EMPTY_ARRAY).some(isBandItem)

  const getBandItem = ({ referenceData }) => {
    const item = (referenceData || EMPTY_ARRAY).find(isBandItem)
    return item
      ? Object.entries(REFERENCE_BAND_DEFAULTS).reduce((result, [name, defaultValue]) => result[name] === undefined ? { ...result, [name]: defaultValue } : result, item)
      : undefined
  }

  const getFormattingKey = ({ item }) => `${REFERENCE_LINE_KEY_PREFIX}${item.id}`

  const getBandKeys = ({ item }) => ({
    area: `${REFERENCE_BAND_KEY_PREFIX}${item.id}_area`,
    lower: `${REFERENCE_BAND_KEY_PREFIX}${item.id}_lower`,
    upper: `${REFERENCE_BAND_KEY_PREFIX}${item.id}_upper`
  })

  // What a percentile computation needs from each percentile line, without the rest of the model.
  const getPercentileLines = ({ items }) => (items || EMPTY_ARRAY)
    .filter(isPercentileLine)
    .map(item => ({ axis: item.axis, key: getFormattingKey({ item }), percentile: item.yValue }))

  const getLineDay = ({ item, today }) => {
    if(isTodayLine(item)) {
      return dayjs.utc(today).format(DAY_FORMAT)
    }
    return isXValueLine({ item, xValue: REFERENCE_X_VALUES.SPECIFIC_DATE.value }) && item.date ? dayjs.utc(item.date).format(DAY_FORMAT) : undefined
  }

  // The days Today and Specific date lines are drawn on; chart sampling keeps their rows.
  const getReferenceDays = ({ items, today }) => Array.from(new Set((items || EMPTY_ARRAY).map(item => getLineDay({ item, today })).filter(Boolean))).sort()

  const getYValueOptionKey = ({ yValue, yValueType }) => `${yValueType}_${yValue}`

  const getYValueOptions = () => [{ label: "0", yValue: 0, yValueType: REFERENCE_Y_VALUE_TYPES.CONSTANT }]
    .concat(Object.values(SPREAD_BAND_RANGE).map(({ label, value }) => ({ label, yValue: value, yValueType: REFERENCE_Y_VALUE_TYPES.PERCENTILE })))
    .map(option => ({ ...option, value: getYValueOptionKey(option) }))

  const parseCreatedYValue = input => {
    const trimmed = typeof input === "string" ? input.trim() : ""
    return CREATED_Y_VALUE_PATTERN.test(trimmed) ? Number(trimmed) : undefined
  }

  const getUsedPercentiles = ({ axis, exceptId, referenceData }) => (referenceData || EMPTY_ARRAY)
    .filter(item => isLineItem(item) && item.axis === axis && item.id !== exceptId && item.yValueType === REFERENCE_Y_VALUE_TYPES.PERCENTILE)
    .map(item => item.yValue)

  // The band range mapper resolves any percentile option, not only the band limits.
  const getPercentileLabel = value => basicCustomInsightConsts.getParamMapper({ paramName: "bandMin", type: TILE_TYPES.CHART })(value)?.label

  const getXValueLabel = ({ item }) => item.xValue === REFERENCE_X_VALUES.SPECIFIC_DATE.value && item.date
    ? item.date
    : Object.values(REFERENCE_X_VALUES).find(option => option.value === item.xValue)?.label

  const getYValueLabel = ({ item }) => {
    if(item.yValue === undefined) {
      return undefined
    }
    return item.yValueType === REFERENCE_Y_VALUE_TYPES.PERCENTILE ? getPercentileLabel(item.yValue) : String(item.yValue)
  }

  // Only shown while the line has no value yet; once it has one, the value names it.
  const getLineLabel = ({ item }) => [
    REFERENCE_DATA_TYPES.LINE.label,
    Object.values(REFERENCE_LINE_AXES).find(axis => axis.value === item.axis)?.label
  ].filter(Boolean).join(LABEL_SEPARATOR)

  const getBandLabel = ({ item }) => {
    const bandItem = getBandItem({ referenceData: [item] })
    return `${REFERENCE_DATA_TYPES.BAND.label}${LABEL_SEPARATOR}${getPercentileLabel(bandItem.bandMin)} – ${getPercentileLabel(bandItem.bandMax)}`
  }

  const hasLineValue = ({ item }) => item.axis === REFERENCE_LINE_AXES.X.value ? item.xValue !== undefined : isYAxisLine(item) && item.yValue !== undefined

  // The chosen value names a line until the user renames it.
  const getValueLabel = ({ item }) => {
    if(!isLineItem(item) || !hasLineValue({ item })) {
      return undefined
    }
    return item.axis === REFERENCE_LINE_AXES.X.value ? getXValueLabel({ item }) : getYValueLabel({ item })
  }

  const getItemLabel = ({ item }) => {
    if(item?.label) {
      return item.label
    }
    if(isBandItem(item)) {
      return getBandLabel({ item })
    }
    return isLineItem(item) ? getValueLabel({ item }) || getLineLabel({ item }) : "Reference data"
  }

  const getLineDefaults = ({ item }) => item.yValueType === REFERENCE_Y_VALUE_TYPES.PERCENTILE && isYAxisLine(item)
    ? { color: basicColors["grey-dark"], lineOpacity: .8, lineThickness: 2 }
    : { color: basicColors["grey-medium"], lineThickness: 2 }

  const getLineFormatting = ({ item }) => [{
    applyTo: getFormattingKey({ item }),
    chartType: CHART_TYPES.LINE.value,
    label: getItemLabel({ item }),
    lineStyle: LINE_STYLES.DASHED.value,
    showInLegend: true,
    ...(isDateLabelLine(item) ? DATE_LINE_FORMATTING : {}),
    ...getLineDefaults({ item })
  }]

  const getBandFormatting = ({ item }) => {
    const { area, lower, upper } = getBandKeys({ item }),
          lineDefaults = { chartType: CHART_TYPES.LINE.value, color: basicColors["grey-medium"], lineOpacity: 1, lineStyle: LINE_STYLES.SOLID.value, lineThickness: 1, showInLegend: true }
    return [
      { applyTo: lower, label: REFERENCE_BAND_LABELS.lower, ...lineDefaults },
      { applyTo: upper, label: REFERENCE_BAND_LABELS.upper, ...lineDefaults },
      { applyTo: area, chartType: CHART_TYPES.AREA.value, color: basicColors["grey-medium"], label: REFERENCE_BAND_LABELS.area, showInLegend: true }
    ]
  }

  const getDefaultFormatting = ({ item }) => {
    if(isBandItem(item)) {
      return getBandFormatting({ item })
    }
    return isLineItem(item) && hasLineValue({ item }) ? getLineFormatting({ item }) : EMPTY_ARRAY
  }

  const getItemFormatting = ({ conditionalFormatting, item }) => (conditionalFormatting || EMPTY_ARRAY).find(entry => entry?.applyTo === getFormattingKey({ item }))

  const getAllDefaultFormatting = ({ referenceData }) => (referenceData || EMPTY_ARRAY).flatMap(item => getDefaultFormatting({ item }))

  // Conditional formatting lists every reference entry under the item's label.
  const getFormattingApplyToOptions = ({ referenceData }) => getAllDefaultFormatting({ referenceData })
    .map(({ applyTo, label }) => ({ label, value: applyTo }))

  // Picking a reference entry in Conditional formatting replaces the styling a previous target left behind.
  const FORMATTING_RESET = { colorScale: undefined, conditionalColorPalette: undefined, descendingOpacity: undefined, lineOpacity: undefined, lineStyle: undefined, lineThickness: undefined, opacityStep: undefined }

  const getFormattingDefaults = ({ applyTo, referenceData }) => {
    const formatting = getAllDefaultFormatting({ referenceData }).find(entry => entry.applyTo === applyTo)
    return formatting
      ? Object.fromEntries(Object.entries({ ...FORMATTING_RESET, ...formatting }).filter(([name]) => name !== "applyTo" && name !== "label"))
      : undefined
  }

  const getExpiryLine = ({ id }) => ({ axis: REFERENCE_LINE_AXES.X.value, id, type: REFERENCE_DATA_TYPES.LINE.value, xValue: REFERENCE_X_VALUES.EXPIRY_DATE.value })

  const isExpiryDateAvailable = ({ isFileLike, isSeasonality, seasonalityType }) => isSeasonality && seasonalityType === SEASONALITY_TYPE.EXPIRY.value && !isFileLike

  // The legacy band fields are the band item fields.
  const LEGACY_BAND_FIELDS = Object.keys(REFERENCE_BAND_DEFAULTS)
  const LEGACY_FIELDS = LEGACY_BAND_FIELDS.concat(["isBandEnabled", "seasonalityIsMedianLineEnabled", "seasonalityMedianLineValue"])
  const LEGACY_MEDIAN_LABEL = "Median"

  // Legacy tiles may still hold the source as a plain string and the source tile id in sourceInputParamsId.
  const isFileLikeTile = ({ inputParams, tile }) => ChartSourceService.isFileLikeSource({
    inputParamsType: inputParams?.[tile.source?.value?.id || tile.sourceInputParamsId]?.basicInputParamsType,
    sourceType: (typeof tile.source === "string" ? tile.source : tile.source?.type) || CHART_SOURCES.DATA_PREP.value
  })

  const getBandMigration = ({ generateId, tile }) => {
    const item = LEGACY_BAND_FIELDS
            .filter(field => tile[field] !== undefined)
            .reduce((result, field) => ({ ...result, [field]: tile[field] }), { id: generateId(), type: REFERENCE_DATA_TYPES.BAND.value }),
          { area, lower, upper } = getBandKeys({ item })
    return { item, keys: { [SPREAD_BAND_KEY]: area, [SPREAD_LOWER_BAND_KEY]: lower, [SPREAD_UPPER_BAND_KEY]: upper } }
  }

  // In spread mode the worker drew the median when its flag was never set and the band was on.
  const isLegacySpreadMedian = ({ tile }) => tile.seasonalityType === SEASONALITY_TYPE.EXPIRY.value
    && tile.seasonalityIsSpreadEnabled === true
    && tile.seasonalityIsMedianLineEnabled === undefined
    && tile.isBandEnabled === true
    && (tile.rightAxis?.data || EMPTY_ARRAY).length > 0

  const getMedianMigration = ({ generateId, tile }) => {
    const value = Number(tile.seasonalityMedianLineValue),
          item = {
            axis: REFERENCE_LINE_AXES.LEFT.value,
            id: generateId(),
            // The legacy median was listed as "Median" whatever its percentile.
            label: LEGACY_MEDIAN_LABEL,
            type: REFERENCE_DATA_TYPES.LINE.value,
            yValue: tile.seasonalityMedianLineValue === undefined || !Number.isFinite(value) ? SPREAD_BAND_RANGE.PERC_50.value : value,
            yValueType: REFERENCE_Y_VALUE_TYPES.PERCENTILE
          }
    return { item, keys: { [SPREAD_MEDIAN_KEY]: getFormattingKey({ item }) } }
  }

  const getExpiryMigration = ({ generateId }) => {
    const item = getExpiryLine({ id: generateId() })
    return { addMissingFormatting: true, formatting: DATE_LINE_FORMATTING, item, keys: { [EXPIRY_DATE_REFERENCE_LINE_KEY]: getFormattingKey({ item }) } }
  }

  // normalizeChartTileConfig resolves basicChartType (including legacy isSeasonality tiles) before migrating.
  const getTileMigrations = ({ generateId, inputParams, tile }) => {
    const isSeasonality = tile.basicChartType === BASIC_CHART_TYPES.SEASONALITY.value
    return [
      tile.isBandEnabled === true ? getBandMigration({ generateId, tile }) : undefined,
      isSeasonality && (tile.seasonalityIsMedianLineEnabled === true || isLegacySpreadMedian({ tile })) ? getMedianMigration({ generateId, tile }) : undefined,
      isExpiryDateAvailable({ isFileLike: isFileLikeTile({ inputParams, tile }), isSeasonality, seasonalityType: tile.seasonalityType }) ? getExpiryMigration({ generateId }) : undefined
    ].filter(Boolean)
  }

  // Legacy band entries may still carry a colour palette; a band uses a single colour.
  const getSingleColorFormatting = entry => ({
    ...entry,
    color: entry.color || entry.conditionalColorPalette?.colors?.[0] || basicColors["grey-medium"],
    conditionalColorPalette: undefined
  })

  const getMigratedEntry = ({ entry, migration }) => {
    const migratedEntry = { ...entry, applyTo: migration.keys[entry.applyTo], ...(isLineItem(migration.item) ? { label: getItemLabel({ item: migration.item }) } : {}), ...migration.formatting }
    return isBandItem(migration.item) ? getSingleColorFormatting(migratedEntry) : migratedEntry
  }

  const getMigratedFormatting = ({ conditionalFormatting = EMPTY_ARRAY, migrations }) => {
    const relabelled = conditionalFormatting.map(entry => {
      const migration = migrations.find(({ keys }) => keys[entry?.applyTo] !== undefined)
      return migration ? getMigratedEntry({ entry, migration }) : entry
    })
    const missing = migrations
      .filter(({ addMissingFormatting, keys }) => addMissingFormatting && !conditionalFormatting.some(entry => keys[entry?.applyTo] !== undefined))
      .flatMap(({ formatting, item }) => getDefaultFormatting({ item }).map(entry => ({ ...entry, ...formatting })))
    return relabelled.concat(missing)
  }

  const getMigratedTile = ({ generateId = CommonService.generateUID, inputParams = {}, tile }) => {
    if(tile?.type !== TILE_TYPES.CHART || Array.isArray(tile.referenceData)) {
      return tile
    }
    const migrations = getTileMigrations({ generateId, inputParams, tile }),
          conditionalFormatting = getMigratedFormatting({ conditionalFormatting: tile.conditionalFormatting, migrations }),
          migratedTile = {
            ...tile,
            referenceData: migrations.map(({ item }) => item),
            ...(tile.conditionalFormatting !== undefined || conditionalFormatting.length > 0 ? { conditionalFormatting } : {}),
            ...(Array.isArray(tile.defaultYears) ? { defaultYears: tile.defaultYears.filter(year => year !== SPREAD_MEDIAN_KEY) } : {})
          }
    LEGACY_FIELDS.forEach(field => delete migratedTile[field])
    return migratedTile
  }

  return {
    getBandItem,
    getBandKeys,
    getDefaultFormatting,
    getExpiryLine,
    getFormattingApplyToOptions,
    getFormattingDefaults,
    getFormattingKey,
    getItemFormatting,
    getItemLabel,
    getLineLabelFormatting,
    getMigratedTile,
    getPercentileLines,
    getReferenceDays,
    getUsedPercentiles,
    getValueLabel,
    getYValueOptionKey,
    getYValueOptions,
    hasLineValue,
    isBandEnabled,
    isBandItem,
    isDateLabelLine,
    isExpiryDateAvailable,
    isExpiryLine,
    isLineItem,
    isLineKey,
    isPercentileLine,
    isTodayLine,
    parseCreatedYValue
  }
})()
